import { type MotionValue } from 'motion/react'
import { type ReactNode } from 'react'
import {
  StickyViewHeader,
  StickyViewHeaderMain,
  StickyViewHeaderExtras,
} from '@/components/StickyViewHeader'
import { Button } from '@/components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import styles from './InventoryManagerHeader.module.css'

export interface InventoryManagerHeaderProps {
  scrollOffset: MotionValue<number>
  heading: ReactNode
  canManageGlobalInventory: boolean
  canManageWorkspaceInventory: boolean
  onAddGlobalItem: () => void
  onAddWorkspaceItem: () => void
}

export function InventoryManagerHeader({
  scrollOffset,
  heading,
  canManageGlobalInventory,
  canManageWorkspaceInventory,
  onAddGlobalItem,
  onAddWorkspaceItem,
}: InventoryManagerHeaderProps) {
  return (
    <StickyViewHeader scrollOffset={scrollOffset}>
      <StickyViewHeaderMain>{heading}</StickyViewHeaderMain>
      <StickyViewHeaderExtras>
        <AddInventoryItemDropdown
          canManageGlobalInventory={canManageGlobalInventory}
          canManageWorkspaceInventory={canManageWorkspaceInventory}
          onAddGlobalItem={onAddGlobalItem}
          onAddWorkspaceItem={onAddWorkspaceItem}
        />
      </StickyViewHeaderExtras>
    </StickyViewHeader>
  )
}

interface AddInventoryItemDropdownProps {
  canManageGlobalInventory: boolean
  canManageWorkspaceInventory: boolean
  onAddGlobalItem: () => void
  onAddWorkspaceItem: () => void
}

function AddInventoryItemDropdown({
  canManageGlobalInventory,
  canManageWorkspaceInventory,
  onAddGlobalItem,
  onAddWorkspaceItem,
}: AddInventoryItemDropdownProps) {
  return (
    <Dropdown
      button={
        <Button
          label="Add Service Item"
          type="submit"
          iconRight="dropdown"
          iconRightFlush
          testId="add-inventory-item-dropdown"
        />
      }
      position="bottom-start"
      autoDismiss
    >
      <DropdownTitle>Add new</DropdownTitle>
      {canManageGlobalInventory && (
        <DropdownItem
          onPress={onAddGlobalItem}
          buttonTestId="add-global-inventory-item"
        >
          Global service item
        </DropdownItem>
      )}
      {canManageWorkspaceInventory && (
        <DropdownItem
          onPress={onAddWorkspaceItem}
          buttonTestId="add-workspace-inventory-item"
        >
          Workspace service item
        </DropdownItem>
      )}
    </Dropdown>
  )
}

export interface InventoryCountLabelProps {
  label: string
  count?: number
}

/**
 * An inventory name followed by its item count, for the heading's dropdown.
 */
export function InventoryCountLabel({
  label,
  count,
}: InventoryCountLabelProps) {
  return (
    <>
      {label} <span className={styles.inventoryCount}>({count})</span>
    </>
  )
}
